import React, { useState } from "react";

export default function ReportCanvas({ reportData, isLoading, scenarioInfo }) {
  // If loading, show a sophisticated skeleton
  if (isLoading) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-4xl mx-auto space-y-8 animate-pulse-slow">
          <div className="h-10 w-2/3 bg-slate-800/80 rounded-lg mx-auto mb-12"></div>
          
          <div className="space-y-4">
            <div className="h-6 w-1/4 bg-slate-800/60 rounded-md"></div>
            <div className="h-4 w-full bg-slate-800/40 rounded-md"></div>
            <div className="h-4 w-full bg-slate-800/40 rounded-md"></div>
            <div className="h-4 w-5/6 bg-slate-800/40 rounded-md"></div>
          </div>

          <div className="space-y-4 mt-8">
            <div className="h-6 w-1/3 bg-slate-800/60 rounded-md"></div>
            <div className="h-4 w-full bg-slate-800/40 rounded-md"></div>
            <div className="h-4 w-4/5 bg-slate-800/40 rounded-md"></div>
          </div>
        </div>
      </div>
    );
  }

  // If no report, show an inspiring empty state
  if (!reportData) {
    return (
      <div className="h-full w-full flex items-center justify-center relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-[100px] animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[120px] animate-float" style={{ animationDelay: "2s" }}></div>
        
        <div className="relative text-center max-w-lg mx-auto z-10 glass-panel p-12 rounded-3xl animate-fade-in-up">
          <div className="text-6xl mb-6 flex justify-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-2xl shadow-indigo-500/20 transform rotate-12 hover:rotate-0 transition-transform duration-500">
              ✨
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-4 tracking-tight">Blank Canvas</h2>
          <p className="text-slate-400 font-medium leading-relaxed">
            Select a report type on the left, fill in the context, and let the AI instantly forge a C-Level ready masterpiece.
          </p>
        </div>
      </div>
    );
  }

  const blocks = reportData.sections || reportData.blocks || [];
  const reportTitle = reportData.report_title || reportData.reportTitle || scenarioInfo?.title || "Untitled Report";

  // Render the Actual Report
  return (
    <div className="h-full w-full overflow-y-auto px-4 py-12 md:px-12 lg:px-24">
      {/* Decorative background glow matching the text */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-indigo-500/5 rounded-full blur-[150px] pointer-events-none"></div>

      <div className="max-w-4xl mx-auto relative z-10 animate-fade-in">
        
        {/* Copilot Header Badge */}
        <div className="flex items-center gap-2 mb-8">
          <span className="px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-xs font-bold tracking-widest uppercase">
            AI Generated
          </span>
          <span className="text-xs text-slate-500 font-medium">{new Date().toLocaleString()}</span>
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white via-indigo-100 to-slate-400 tracking-tight leading-tight mb-12">
          {reportTitle}
        </h1>

        {/* Executive Summary Block */}
        {reportData.executive_summary && (
          <div className="glass-panel p-8 rounded-2xl mb-12 relative overflow-hidden border-indigo-500/30">
            <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500"></div>
            <h3 className="text-lg font-bold text-indigo-300 mb-4 flex items-center gap-2">
              <span className="text-2xl">🎯</span> Executive Summary
            </h3>
            <p className="text-slate-300 leading-relaxed font-medium text-lg whitespace-pre-wrap">
              {reportData.executive_summary}
            </p>
          </div>
        )}

        {/* Dynamic Blocks */}
        <div className="space-y-10">
          {blocks.map((block, idx) => (
            <div key={block.id || idx} className="group">
              <div className="flex flex-col md:flex-row gap-6">
                
                {/* Visual Connector & Bullet */}
                <div className="hidden md:flex flex-col items-center pt-1">
                  <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 font-bold group-hover:bg-indigo-500/20 group-hover:text-indigo-300 group-hover:border-indigo-500/50 transition-colors">
                    {idx + 1}
                  </div>
                  {idx !== blocks.length - 1 && (
                    <div className="w-px h-full bg-slate-800 mt-2 mb-[-2rem] group-hover:bg-indigo-500/30 transition-colors"></div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 glass-card p-8 rounded-2xl">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-slate-100 uppercase tracking-wide">
                      {block.title || block.header}
                    </h2>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      {/* Placeholder for future block actions (e.g. rewrite, copy) */}
                      <button className="text-xs px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-md border border-slate-700">
                        Edit
                      </button>
                    </div>
                  </div>
                  
                  {/* Lines / Content Rendering */}
                  <div className="text-slate-300 text-base leading-loose whitespace-pre-wrap">
                    {block.content ? (
                      block.content
                    ) : block.lines && Array.isArray(block.lines) ? (
                      <ul className="space-y-3">
                        {block.lines.map((line, lIdx) => (
                          <li key={lIdx} className="flex items-start gap-3">
                            <span className="text-indigo-400 mt-1.5 text-xs">◆</span>
                            <span className="flex-1">{line}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer Actions */}
        <div className="mt-16 flex justify-end gap-4 pb-24 border-t border-slate-800 pt-8">
          <button className="px-6 py-3 rounded-xl bg-slate-800 text-white font-medium hover:bg-slate-700 transition">
            Export PDF
          </button>
          <button className="px-6 py-3 rounded-xl bg-indigo-600 text-white font-medium hover:bg-indigo-500 shadow-lg shadow-indigo-500/20 transition">
            Share Report
          </button>
        </div>
      </div>
    </div>
  );
}
